import axios from 'axios';

const API_BASE_URL = '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 添加认证令牌到所有请求
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 处理响应错误
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token过期或无效，清除本地存储并跳转登录页
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export type ApiErrorType = 'validation' | 'network' | 'server' | 'auth' | 'unknown';

export interface ApiErrorInfo {
  type: ApiErrorType;
  status: number | null;
  label: string;
  message: string;
  backendMessage: string | null;
  raw: any;
}

export function formatApiError(error: any, operationLabel = 'Operation'): ApiErrorInfo {
  const isAxios = !!(error && error.isAxiosError);
  const response = error?.response;
  const request = error?.request;
  const status: number | null = response?.status ?? null;
  const data = response?.data ?? null;

  const backendMessage: string | null =
    (data && (typeof data.error === 'string' ? data.error : null)) ||
    (data && (typeof data.message === 'string' ? data.message : null)) ||
    (data && Array.isArray(data.details) ? data.details.join('\n') : null) ||
    null;

  let type: ApiErrorType = 'unknown';
  if (isAxios && !response && (request || !error?.code || error?.code === 'ERR_NETWORK')) {
    type = 'network';
  } else if (status === 401 || status === 403) {
    type = 'auth';
  } else if (status !== null && (status === 400 || status === 422 || (status >= 404 && status < 500))) {
    type = 'validation';
  } else if (status !== null && status >= 500) {
    type = 'server';
  }

  const labelMap: Record<ApiErrorType, string> = {
    validation: '❌ Validation Error',
    network: '🌐 Network Error',
    server: '💥 Server Error',
    auth: '🔒 Auth Error',
    unknown: '⚠️ Error',
  };

  const fallbackMap: Record<ApiErrorType, string> = {
    validation: 'Please check the fields and try again.',
    network: 'Cannot connect to server. Please check your network, VPN, or whether backend is running.',
    server: 'Something went wrong on server side. Please check browser console for full details.',
    auth: 'Authentication failed. Please login again.',
    unknown: 'An unexpected error occurred. Please check browser console for full details.',
  };

  const statusStr = status !== null ? ` (HTTP ${status})` : '';
  const message = backendMessage || fallbackMap[type];

  // Log the full error (do NOT swallow failed response body)
  console.error(`[API ERROR] ${operationLabel}${statusStr}`, {
    type,
    status,
    url: error?.config?.url ?? null,
    method: error?.config?.method ?? null,
    params: error?.config?.params ?? null,
    requestData: error?.config?.data ?? null,
    requestHeaders: error?.config?.headers ?? null,
    statusText: response?.statusText ?? null,
    responseData: data,
    backendMessage,
    code: error?.code ?? null,
    message: error?.message ?? null,
    stack: error?.stack ?? null,
    rawError: error,
  });

  return {
    type,
    status,
    label: `${labelMap[type]}${statusStr}`,
    message,
    backendMessage,
    raw: error,
  };
}

export interface Company {
  id: number;
  entity_id?: string;
  company_name: string;
  state?: string;
  entity_type?: string;
  entity_jurisdiction?: string;
  category?: string;
  nickname?: string;
  federal_tax_id?: string;
  state_tax_id?: string;
  sos_id?: string;
  boir_id?: string;
  duns?: string;
  registration_date?: string;
  formation_document_date?: string;
  status?: string;
  sos_registrant?: string;
  next_file?: string;
  sos_filing_date?: string;
  owner_member?: string;
  ceo_manager?: string;
  ceo_manager_role?: string;
  ceo_manager_role_override?: boolean;
  cfo_tax_rep?: string;
  cfo?: string;
  tax_rep?: string;
  secretary?: string;
  director?: string;
  principal_address?: string;
  mailing_address?: string;
  state_office_address?: string;
  registered_agent_name?: string;
  registered_agent_address?: string;
  business_description?: string;
  naics_code?: string;
  naics_description?: string;
  sic_code?: string;
  sic_description?: string;
  sos_renewal_comment?: string;
  by_law?: string;
  stock_documents?: string;
  company_kit?: string;
  termination_date?: string;
  check_mark?: string;
  operating_agreement?: string;
  follow_up?: string;
  payment?: string;
  ein_letter?: string;
  irs_registrant?: string;
  employee_count?: string;
  website?: string;
  username?: string;
  password?: string;
  question?: string;
  six_month?: string;
  five_month?: string;
  cn_4p?: string;
  us_manager?: string;
  group?: string;
  group_name?: string;
  has_us_staff?: string;
  tier?: string;
  notes?: string;
  remark?: string;
  created_at: string;
  updated_at: string;
  deleted_at?: string;
  deleted_reason?: string;
}

export interface License {
  id: number;
  company_id?: number;
  company_name: string;
  location?: string;
  state?: string;
  level?: string;
  department?: string;
  category?: string;
  license_type?: string;
  certificate_name?: string;
  license_no?: string;
  registration_date?: string;
  status?: string;
  renewal_type?: string;
  expiration_date: string;
  renewal_date?: string;
  renewal_fee?: number;
  conv_fee?: number;
  fee_comment?: string;
  payment_method?: string;
  renewal_method?: string;
  renewal_guide?: string;
  renewal_info?: string;
  info_period?: string;
  info_provider?: string;
  notice_invoice?: string;
  website?: string;
  account?: string;
  password_pin?: string;
  questions?: string;
  contact_person?: string;
  phone_number?: string;
  contact_email?: string;
  follow_up?: string;
  year_2017?: string;
  year_2018?: string;
  year_2019?: string;
  year_2020?: string;
  year_2021?: string;
  year_2022?: string;
  year_2023?: string;
  year_2024?: string;
  year_2025?: string;
  year_2026?: string;
  remark?: string;
  deletion_reason?: string;
  external_coin?: string;
  internal_coin?: string;
  qtr?: string;
  follow_up_date?: string;
  cancelled_date?: string;
  created_at: string;
  updated_at: string;
  deleted_at?: string;
  deleted_reason?: string;
}

export interface Expense {
  id: number;
  application_date: string;
  expense_company: string;
  expense_purpose: string;
  warehouse: string;
  unit_price: number;
  quantity: number;
  unit: string;
  subtotal: number;
  payment_method: string;
  expense_cycle: string;
  approval_text: string;
  approval_role: string;
  approver: string;
  status: string;
  remark: string;
  creator: string;
  created_at: string;
  updated_at: string;
}

export interface ApprovalRule {
  short_name: string;
  company_name: string;
  stage: string;
  level1_limit: number;
  level2_limit: number;
}

export interface Todo {
  id: number;
  title: string;
  description: string;
  priority: string;
  status: string;
  due_date: string;
  assignee: string;
  license_id?: number;
  license_name?: string;
  license_expiration_date?: string;
  task_type?: string;
  next_follow_up_date?: string;
  note?: string;
  created_at: string;
  updated_at: string;
}

export interface User {
  id: number;
  username: string;
  name: string;
  email?: string;
  role: string;
  role_id?: number;
  ome_subject?: string | null;
  is_deleted?: boolean;
  created_at: string;
  updated_at: string;
}

export interface Role {
  id: number;
  name: string;
  description?: string;
  permissions?: Permission[];
  created_at: string;
  updated_at: string;
}

export interface Permission {
  id: number;
  name: string;
  description?: string;
  resource: string;
  action: string;
  created_at: string;
  updated_at: string;
}

export interface LicenseStats {
  total: number;
  expired: number;
  expiring30Days: number;
  expiring60Days: number;
  valid: number;
  byCategory: { category: string; count: number }[];
  byStatus: { status: string; count: number }[];
  byCompany: { company_name: string; count: number }[];
}

export interface CompanyStats {
  total: number;
  totalRows?: number;
  byStage: { stage: string; count: number }[];
  byRegion: { region: string; count: number }[];
  classification?: { classification: string; LLC: number; INC: number; LIMITED: number; Total: number }[];
  entityScope?: string;
}

export interface ExpenseStats {
  totalAmount: number;
  byType: Record<string, number>;
  byCompany: Record<string, number>;
  byMonth: Record<string, number>;
}

export interface DuplicateAnalysisMember {
  id: number;
  entity_id?: string;
  company_name: string;
  nickname?: string;
  entity_type?: string;
  entity_jurisdiction?: string;
  state?: string;
  status?: string;
  group_name?: string;
}

export interface DuplicateAnalysisGroup {
  id: string;
  members: DuplicateAnalysisMember[];
  matchReasons: string[];
  confidence: 'High' | 'Medium' | 'Low';
  suggestedEntityName: string;
}

export interface DuplicateAnalysisData {
  groups: DuplicateAnalysisGroup[];
  totalCompanies: number;
  groupsFound: number;
  companiesInGroups: number;
  note: string;
}

export interface LoginResponse {
  code: number;
  msg: string;
  data: {
    token: string;
    user: {
      id: number;
      username: string;
      name: string;
      email?: string;
      role: string;
    };
  };
}

export interface ApiResponse<T = any> {
  code: number;
  msg: string;
  data: T;
}

export const companyApi = {
  getAll: () => api.get<Company[]>('/company'),
  getDeleted: () => api.get<Company[]>('/company/deleted'),
  getById: (id: number) => api.get<Company>(`/company/${id}`),
  create: (data: Omit<Company, 'id' | 'created_at' | 'updated_at'>) => api.post('/company', data),
  update: (id: number, data: Partial<Company>) => api.put(`/company/${id}`, data),
  delete: (id: number, deleted_reason?: string) => api.delete(`/company/${id}`, { data: { deleted_reason } }),
  restore: (id: number) => api.post(`/company/${id}/restore`),
  permanentDelete: (id: number) => api.delete(`/company/${id}/permanent`),
  search: (keyword: string) => api.get<Company[]>(`/company/search?keyword=${keyword}`),
  export: () => api.get('/company/export', { responseType: 'blob' }),
};

export const licenseApi = {
  getAll: () => api.get<License[]>('/license'),
  getDeleted: () => api.get<License[]>('/license/deleted'),
  getById: (id: number) => api.get<License>(`/license/${id}`),
  create: (data: Omit<License, 'id' | 'created_at' | 'updated_at'>) => api.post('/license', data),
  update: (id: number, data: Partial<License>) => api.put(`/license/${id}`, data),
  delete: (id: number, deleted_reason?: string) => api.delete(`/license/${id}`, { data: { deleted_reason } }),
  restore: (id: number) => api.post(`/license/${id}/restore`),
  permanentDelete: (id: number) => api.delete(`/license/${id}/permanent`),
  search: (keyword: string) => api.get<License[]>(`/license/search?keyword=${keyword}`),
  getExpiringSoon: () => api.get<License[]>('/license/expiring-soon'),
  export: () => api.get('/license/export', { responseType: 'blob' }),
};

export const expenseApi = {
  getAll: () => api.get<Expense[]>('/expense'),
  getById: (id: number) => api.get<Expense>(`/expense/${id}`),
  create: (data: Omit<Expense, 'id' | 'created_at' | 'updated_at' | 'subtotal' | 'status'>) => api.post('/expense', data),
  update: (id: number, data: Partial<Expense>) => api.put(`/expense/${id}`, data),
  delete: (id: number) => api.delete(`/expense/${id}`),
  search: (keyword: string) => api.get<Expense[]>(`/expense/search?keyword=${keyword}`),
  approve: (id: number, data: { approver?: string }) => api.put(`/expense/${id}/approve`, data),
  reject: (id: number, data: { approver?: string }) => api.put(`/expense/${id}/reject`, data),
  getApprovalRules: () => api.get<ApprovalRule[]>('/expense/approval-rules'),
  getApprovalRule: (companyShortName: string) => api.get<ApprovalRule>(`/expense/approval-rules/${companyShortName}`),
  calculateApproval: (data: { companyShortName: string; amount: number }) => api.post('/expense/calculate-approval', data),
  getCompanyShortName: (companyName: string) => api.get<{ short_name: string }>(`/expense/company-short-name?companyName=${encodeURIComponent(companyName)}`),
  export: () => api.get('/expense/export', { responseType: 'blob' }),
};

export const todoApi = {
  getAll: () => api.get<Todo[]>('/todo'),
  getById: (id: number) => api.get<Todo>(`/todo/${id}`),
  create: (data: Omit<Todo, 'id' | 'created_at' | 'updated_at'>) => api.post('/todo', data),
  update: (id: number, data: Partial<Todo>) => api.put(`/todo/${id}`, data),
  delete: (id: number) => api.delete(`/todo/${id}`),
  toggleStatus: (id: number) => api.put(`/todo/${id}/toggle`),
  generateFromLicenses: (days?: number) => api.post(`/todo/generate-from-licenses?days=${days || 30}`),
  getSummary: () => api.get('/todo/stats/summary'),
};

export const userApi = {
  login: (data: { username: string; password: string }) => api.post<LoginResponse>('/user/login', data),
  getMe: () => api.get<ApiResponse<{ id: number; username: string; name: string; email?: string; role: string }>>('/user/me'),
  getPermissions: () => api.get<ApiResponse<Permission[]>>('/user/permissions'),
  getAll: () => api.get<ApiResponse<User[]>>('/user'),
  getById: (id: number) => api.get<ApiResponse<User>>(`/user/${id}`),
  create: (data: Omit<User, 'id' | 'created_at' | 'updated_at'>) => api.post<ApiResponse<User>>('/user', data),
  update: (id: number, data: Partial<User>) => api.put<ApiResponse<User>>(`/user/${id}`, data),
  delete: (id: number) => api.delete<ApiResponse>(`/user/${id}`),
};

export const roleApi = {
  getAll: () => api.get<ApiResponse<Role[]>>('/role'),
  getById: (id: number) => api.get<ApiResponse<Role>>(`/role/${id}`),
  create: (data: Omit<Role, 'id' | 'created_at' | 'updated_at'>) => api.post<ApiResponse<Role>>('/role', data),
  update: (id: number, data: Partial<Role>) => api.put<ApiResponse>(`/role/${id}`, data),
  delete: (id: number) => api.delete<ApiResponse>(`/role/${id}`),
  getAllPermissions: () => api.get<ApiResponse<Permission[]>>('/role/permissions/all'),
};

export const reportApi = {
  getLicenseStats: () => api.get<ApiResponse<LicenseStats>>('/report/license/stats'),
  getCompanyStats: (scope?: string) => {
    const params = new URLSearchParams();
    if (scope) params.set('scope', scope);
    return api.get<ApiResponse<CompanyStats>>(`/report/company/stats${params.toString() ? '?' + params.toString() : ''}`);
  },
  getDuplicateAnalysis: () => api.get<ApiResponse<DuplicateAnalysisData>>('/report/duplicate-analysis'),
  getExpenseStats: (params?: { year?: string; month?: string }) => api.get<ApiResponse<ExpenseStats>>('/report/expense/stats', { params }),
  getExpiringLicenses: (days?: number) => api.get<ApiResponse<{ list: License[]; total: number; days: number }>>(`/report/license/expiring?days=${days || 30}`),
  getExpiredLicenses: () => api.get<ApiResponse<{ list: License[]; total: number }>>('/report/license/expired'),
};

export interface Person {
  id: number;
  person_name: string;
  display_name?: string;
  english_name: string;
  wecom_display_name?: string;
  normalized_name?: string;
  notes?: string;
  person_status: string;
  company_count?: number;
  role_count?: number;
  companies?: string[];
  company_groups?: string[];
  role_counts?: Record<string, number>;
  role_companies?: Record<string, any[]>;
  role_company_groups?: Record<string, string[]>;
  role_needs_confirmation?: Record<string, boolean>;
}

export interface PersonStatsFilters {
  role?: string;
  company_count_range?: string;
  company_group?: string;
}

export const personApi = {
  getAll: () => api.get<ApiResponse<Person[]>>('/person'),
  getStats: (filters?: PersonStatsFilters) => {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== '') {
          params.append(k, String(v));
        }
      });
    }
    const qs = params.toString();
    return api.get<ApiResponse<Person[]>>(`/person/stats${qs ? `?${qs}` : ''}`);
  },
  getById: (id: number) => api.get<ApiResponse<Person>>(`/person/${id}`),
  create: (data: Partial<Person>) => api.post<ApiResponse<Person>>('/person', data),
  update: (id: number, data: Partial<Person>) => api.put<ApiResponse<Person>>(`/person/${id}`, data),
  delete: (id: number) => api.delete<ApiResponse<any>>(`/person/${id}`),
  search: (q: string) => api.get<ApiResponse<Person[]>>(`/person/search/query?q=${encodeURIComponent(q)}`),
  getByCompany: (companyId: number) => api.get<ApiResponse<any[]>>(`/person/company/${companyId}`),
  addToCompany: (companyId: number, data: { person_id: number; role_type: string; role_label?: string }) =>
    api.post<ApiResponse<any>>(`/person/company/${companyId}`, data),
  removeFromCompany: (companyId: number, personId: number, roleType?: string) =>
    api.delete<ApiResponse<any>>(`/person/company/${companyId}/${personId}${roleType ? `?role_type=${roleType}` : ''}`),
};

export default api;
